import type { ChangeEvent, FC, KeyboardEvent, ReactElement } from 'react';

import type { Nullable } from '@zen/utils/typescript';
import { convertStringToNumber } from '@zen/utils/utils';

import { IconButton } from '../Button';
import type { InputProps } from '../Input';
import Input from '../Input';
import type { StepDirection } from './helpers';
import { canStep, getInputSymbols, getPlaceholder, prepareInputDigitsPattern, stepValue } from './helpers';

interface Props extends Omit<InputProps, 'hasInteractiveIconLeft' | 'hideSpinButtons' | 'isTextCentered' | 'onChange' | 'type'> {
  max?: number | string;
  maxDecimalDigits?: number;
  maxWholeDigits?: number;
  min?: number | string;
  onChange: (value: Nullable<number>) => void;
  showStepperButtons?: boolean;
  step?: string | number;
}

const NumberInput: FC<Props> = (props) => {
  const {
    disabled,
    iconLeft,
    iconRight,
    locked,
    max,
    maxDecimalDigits,
    maxWholeDigits,
    min,
    onChange,
    onKeyDown,
    placeholder,
    showStepperButtons = false,
    step = '0.01',
    value,
    ...rest
  } = props;
  const shouldCheckValueAgainstPattern: boolean = !!maxDecimalDigits || !!maxWholeDigits;
  const symbolsToExclude: string[] = getInputSymbols(maxDecimalDigits, min);
  const isStepperDisabled: boolean = !!disabled || !!locked;

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const testRegex: boolean = prepareInputDigitsPattern(maxDecimalDigits, maxWholeDigits).test(e.target.value);

    if (shouldCheckValueAgainstPattern && !testRegex) {
      e.preventDefault();

      return;
    }

    const newValue: Nullable<number> = convertStringToNumber(e.currentTarget.value);

    onChange(newValue);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (symbolsToExclude.includes(event.key)) {
      event.preventDefault();
    }

    onKeyDown?.(event);
  };

  const handleStep = (direction: StepDirection): void => {
    onChange(stepValue(value, direction, { max, min, step }));
  };

  const decrementButton: ReactElement = (
    <IconButton
      disabled={isStepperDisabled || !canStep(value, -1, { max, min, step })}
      icon="zicon-minus"
      onClick={(): void => handleStep(-1)}
      showTooltip={false}
      size="medium"
      title="Decrease value"
      variant="ghost"
    />
  );

  const incrementButton: ReactElement = (
    <IconButton
      disabled={isStepperDisabled || !canStep(value, 1, { max, min, step })}
      icon="zicon-add"
      onClick={(): void => handleStep(1)}
      showTooltip={false}
      size="medium"
      title="Increase value"
      variant="ghost"
    />
  );

  return (
    <Input
      {...rest}
      data-component="number-input"
      disabled={disabled}
      hasInteractiveIconLeft={showStepperButtons}
      hideSpinButtons={showStepperButtons}
      iconLeft={showStepperButtons ? decrementButton : iconLeft}
      iconRight={showStepperButtons ? incrementButton : iconRight}
      isTextCentered={showStepperButtons}
      locked={locked}
      max={max}
      min={min}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={getPlaceholder(maxDecimalDigits, placeholder)}
      step={step}
      type="number"
      value={value}
    />
  );
};

export type { Props as NumberInputProps };

export default NumberInput;
